import React, { useEffect } from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';
import Portal from '../Portal';
import { useConfirmClientStore } from '../../store/confirmClientStore';

/**
 * Popup affiché quand un agent passe un post en "À valider" : prévient que le client (relié au
 * contrat) va voir le post sur son espace. Monté une seule fois (App root) → vaut pour tous les agents.
 */
const ConfirmClientWillSeeModal: React.FC = () => {
  const { open, clientName, confirm, cancel } = useConfirmClientStore();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel();
      if (e.key === 'Enter') confirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirm, cancel]);

  if (!open) return null;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,.5)' }}
        onClick={cancel}
      >
        <div
          className="nudge-detail w-full max-w-sm rounded-2xl overflow-hidden"
          style={{ background: 'var(--ev-panel)', boxShadow: 'var(--ev-shadow), inset 0 0 0 1px var(--ev-line)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--ev-accent-soft)', color: 'var(--ev-accent)' }}
              >
                <EyeIcon className="w-5 h-5" />
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 className="text-base font-semibold" style={{ color: 'var(--ev-text)' }}>
                  C'est bien finalisé ?
                </h3>
                <p className="text-sm mt-1 leading-snug" style={{ color: 'var(--ev-muted)' }}>
                  <span style={{ color: 'var(--ev-text)', fontWeight: 600 }}>{clientName}</span> va voir ce post
                  sur son espace client pour le valider. Assure-toi qu'il est prêt.
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2.5 px-5 pb-5">
            <button
              onClick={cancel}
              className="flex-1 h-11 rounded-xl text-sm font-medium"
              style={{ background: 'var(--ev-raised)', color: 'var(--ev-text)' }}
            >
              Annuler
            </button>
            <button
              onClick={confirm}
              className="flex-1 h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
              style={{ background: 'var(--ev-accent)', color: '#fff' }}
            >
              <EyeIcon className="w-4 h-4" /> Envoyer au client
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default ConfirmClientWillSeeModal;
